import { useEffect, useRef } from 'react';
import findIndex from 'lodash/findIndex';
import { comparer } from 'mobx';

import { TitleParams } from '@components/Tabs';
import { useQueryFilters } from '@hooks/useQueryFilters';
import { useUpdatingRef } from '@hooks/useUpdatingRef';
import { store } from '@store';
import { translate } from '@chaika/i18n/helper';
import { tabsNavigationStore } from '../store';

import type { TranslationValues } from '@chaika/i18n/types';
import type { PagesParams } from '../TabsNavigationPage';

interface Params {
    pages: PagesParams[];
    tabTitle?: TranslationValues | string;
    onTabSelect(index: number): void;
}

interface Data {
    isOnLoad: boolean;
    selectedPageParam: PagesParams | undefined;
    tabTitle: TranslationValues | string | undefined;
}

export function useTabsNavigationPage({ pages, tabTitle, onTabSelect }: Params): Data {
    const { getQueryFilterParams } = useQueryFilters<{ currentPage?: string }>();
    const navigationTabs = tabsNavigationStore.navigationTabs;

    const prevPageTitleParamsRef = useRef<TitleParams[] | undefined>(undefined);

    // The store keeps the first registered onTabClick, so it must go through a ref
    // to always call the latest onTabSelect (with fresh closure state) on tab clicks.
    const onTabSelectRef = useUpdatingRef(onTabSelect);

    const pageTitleParams = getPageTitleParams(pages);

    const handlePageSelect = (index: number): void => {
        navigationTabs.setActiveIndex(index);
        onTabSelectRef.current(index);
    };

    const getSelectedIndex = (): number => {
        const { currentPage } = getQueryFilterParams();
        const index = currentPage ? findIndex(pages, { name: currentPage }) : 0;

        return index !== -1 ? index : 0;
    };

    const changeTabs = (titleParams: TitleParams[]): void => {
        const selectedPage = getSelectedIndex();

        onTabSelect(selectedPage);
        navigationTabs.setNavigationTabs({
            titleParams,
            activeIndex: selectedPage,
            onTabClick: handlePageSelect,
        });
    };

    useEffect(() => {
        const isFirstRun = prevPageTitleParamsRef.current === undefined;
        const titleParamsChanged = isFirstRun || !comparer.structural(prevPageTitleParamsRef.current, pageTitleParams);

        if (titleParamsChanged) {
            prevPageTitleParamsRef.current = pageTitleParams;
            changeTabs(pageTitleParams);
        } else {
            const expected = getSelectedIndex();

            if (navigationTabs.activeIndex !== expected) {
                navigationTabs.setActiveIndex(expected);
            }
        }
    });

    useEffect(() => () => navigationTabs.reset(), [navigationTabs]);

    const selectedPageParam =
        pages.find(page => page.name === getQueryFilterParams().currentPage) ?? pages[navigationTabs.activeIndex];

    return {
        isOnLoad: store.app.isOnLoad,
        selectedPageParam,
        tabTitle: pages.length ? (tabTitle ?? translate(pages[navigationTabs.activeIndex]?.title)) : undefined,
    };
}

function getPageTitleParams(pages: PagesParams[]): TitleParams[] {
    return pages.map(({ title, dataTest }) => ({ title, dataTest }));
}
